/**
 * AI0a — assistive_only mutation block registry.
 *
 * SSOT: docs/inflomatrix-constitution/ui-architecture/ai-governance-baseline.json
 */

import {
  createAssistiveMutationBlockedError,
  createInvalidAssistiveActionError,
} from './assistive-policy-error';

export const ASSISTIVE_ONLY_MODE = 'assistive_only' as const;

export const ASSISTIVE_ONLY_BLOCKED_MUTATIONS = [
  'execute',
  'approve',
  'delete',
  'changeFinance',
] as const;

export type AssistiveOnlyBlockedMutation = (typeof ASSISTIVE_ONLY_BLOCKED_MUTATIONS)[number];

export function isAssistiveOnlyBlockedMutation(
  action: string
): action is AssistiveOnlyBlockedMutation {
  return (ASSISTIVE_ONLY_BLOCKED_MUTATIONS as readonly string[]).includes(action);
}

export function assertAssistiveOnlyAllowsAction(mode: string | undefined, action: string): void {
  if (mode === ASSISTIVE_ONLY_MODE && isAssistiveOnlyBlockedMutation(action)) {
    throw createAssistiveMutationBlockedError(action);
  }
}

export function assertAssistiveCapabilityActionAllowed(
  mode: string | undefined,
  action: string
): void {
  const normalized = action.trim();
  if (!normalized) {
    return;
  }
  if (mode === 'disabled') {
    throw createInvalidAssistiveActionError(normalized);
  }
  assertAssistiveOnlyAllowsAction(mode, normalized);
}

export type ServerDerivedAssistiveMode = typeof ASSISTIVE_ONLY_MODE | 'disabled';

/**
 * AI gateway server-authority derivation — the client request body is never trusted
 * as the source of truth for `assistiveMode`.
 *
 * Every tenant pilot AI mode is `assistive_only` (see `TENANT_PILOT_AI_MODES` SSOT); the
 * server therefore always derives `assistive_only` as the enforced baseline. A client MAY
 * request the strictly stricter `disabled` state (fail closed — blocks every capability
 * action, not just mutations), but it can never widen the baseline: an omitted, malformed,
 * or otherwise non-`disabled` client value resolves to `assistive_only`, so leaving the
 * field out of a request body cannot bypass the mutation guard.
 */
export function resolveServerAssistiveModeForAiGateway(
  clientAssistiveMode?: string
): ServerDerivedAssistiveMode {
  if (clientAssistiveMode === 'disabled') {
    return 'disabled';
  }
  return ASSISTIVE_ONLY_MODE;
}
